import { apiCall, isBackendConfigured } from './api-call'
import {
  QuickPassThreadMessage,
  QuickPassThreadSummary,
} from '../_types/quickpass-message'

/**
 * Lists the renter's message threads (one per inquired property). Returns an
 * empty list when the backend isn't configured.
 *
 * @param {string} quickPassUserId
 * @returns {Promise<QuickPassThreadSummary[]>}
 */
const getMessageThreads = async (
  quickPassUserId: string,
): Promise<QuickPassThreadSummary[]> => {
  if (isBackendConfigured() === false) {
    return []
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'getmessagethreads',
      data: { quickPassUserId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as QuickPassThreadSummary[]
  } catch (error) {
    console.warn('[messages] getMessageThreads failed —', error)
    return []
  }
}

/**
 * Loads the cleaned message history for one property thread.
 *
 * @param {string} quickPassUserId
 * @param {string} entityId
 * @returns {Promise<QuickPassThreadMessage[]>}
 */
const getThreadMessages = async (
  quickPassUserId: string,
  entityId: string,
): Promise<QuickPassThreadMessage[]> => {
  if (isBackendConfigured() === false) {
    return []
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'getthreadmessages',
      data: { quickPassUserId, entityId },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as QuickPassThreadMessage[]
  } catch (error) {
    console.warn('[messages] getThreadMessages failed —', error)
    return []
  }
}

/**
 * Sends a renter message into a property thread. Resolves once the Casa
 * pipeline has run; the returned reply is the AI's response (empty when the
 * property manager has paused the AI). Returns null on failure.
 *
 * @param {string} quickPassUserId
 * @param {string} entityId
 * @param {string} message
 * @returns {Promise<{ reply: string } | null>}
 */
const sendMessage = async (
  quickPassUserId: string,
  entityId: string,
  message: string,
): Promise<{ reply: string } | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'sendmessage',
      data: { quickPassUserId, entityId, message },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as { reply: string }
  } catch (error) {
    console.warn('[messages] sendMessage failed —', error)
    return null
  }
}

/**
 * Marks a property thread read (up to now) so its unread count clears.
 *
 * @param {string} quickPassUserId
 * @param {string} entityId
 * @returns {Promise<void>}
 */
const markThreadRead = async (
  quickPassUserId: string,
  entityId: string,
): Promise<void> => {
  if (isBackendConfigured() === false) {
    return
  }

  try {
    await apiCall({
      group: 'quickpass',
      command: 'markthreadread',
      data: { quickPassUserId, entityId },
    })
  } catch (error) {
    console.warn('[messages] markThreadRead failed —', error)
  }
}

export { getMessageThreads, getThreadMessages, sendMessage, markThreadRead }
